/**
 * Data for the shared entity activity feed (D3).
 *
 * Owns the list, the filter, paging and the three states. Kept out of
 * the component so the seven detail pages share one loading contract
 * and the component stays about rendering (CLAUDE.md Regel 48).
 *
 * The stale-response guard is the interesting part. A user who opens
 * tenant A, then tenant B before A's request lands, must not see A's
 * timeline under B's name. Every request carries a sequence number and
 * a late reply whose number is not the latest is dropped — on the first
 * page, on a filter change and on "Mehr laden" alike.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { fetchAdminActivityFeed } from "../../../api/adminData";

export const ACTIVITY_PAGE_SIZE = 20;

/** The filter chips, in display order. `null` means "no narrowing" and
 * is deliberately not sent to the server. */
export const ACTIVITY_FILTERS = [
  { key: "all", value: null, label: "Alle" },
  { key: "task", value: "task", label: "Aufgaben" },
  { key: "work_report", value: "work_report", label: "Rapporte" },
  { key: "note", value: "note", label: "Notizen" },
  { key: "change", value: "change", label: "Änderungen" },
];

export function useEntityActivityFeed({ entityType, entityId, refreshKey = 0 }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [loadMoreError, setLoadMoreError] = useState(null);

  // Monotonic guard: only the newest request may write state.
  const seqRef = useRef(0);

  const activeValue =
    ACTIVITY_FILTERS.find((f) => f.key === filter)?.value ?? null;

  /** Reset BEFORE the load effect runs, so switching entity or filter
   * never shows the previous one's rows for a frame. Declared first on
   * purpose — effect order is source order. */
  useEffect(() => {
    setItems([]);
    setTotal(0);
    setHasMore(false);
    setError(null);
    setLoadMoreError(null);
  }, [entityType, entityId, filter]);

  const loadFirstPage = useCallback(async () => {
    if (!entityId || !entityType) return;
    const seq = ++seqRef.current;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAdminActivityFeed({
        entity_type: entityType,
        entity_id: entityId,
        ...(activeValue ? { activity_type: activeValue } : {}),
        limit: ACTIVITY_PAGE_SIZE,
        offset: 0,
      });
      if (seq !== seqRef.current) return;
      setItems(Array.isArray(data?.items) ? data.items : []);
      setTotal(typeof data?.total === "number" ? data.total : 0);
      setHasMore(Boolean(data?.has_more));
    } catch (err) {
      if (seq !== seqRef.current) return;
      setError(err?.message || "Aktivitäten konnten nicht geladen werden.");
      setItems([]);
    } finally {
      if (seq === seqRef.current) setLoading(false);
    }
  // refreshKey is deliberately a cache-buster: it re-mints the callback
  // so the load effect re-runs after a sibling mutation (REPORT-UX1).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId, activeValue, refreshKey]);

  useEffect(() => {
    void loadFirstPage();
  }, [loadFirstPage]);

  /** Append the next page. A failure here keeps what is already on
   * screen — losing a loaded timeline because page three failed would
   * be worse than the missing page. */
  const loadMore = useCallback(async () => {
    if (!entityId || loadingMore || !hasMore) return;
    const seq = seqRef.current;
    setLoadingMore(true);
    setLoadMoreError(null);
    try {
      const data = await fetchAdminActivityFeed({
        entity_type: entityType,
        entity_id: entityId,
        ...(activeValue ? { activity_type: activeValue } : {}),
        limit: ACTIVITY_PAGE_SIZE,
        offset: items.length,
      });
      if (seq !== seqRef.current) return;
      const next = Array.isArray(data?.items) ? data.items : [];
      // Defensive de-dup by activity id: the backend sorts stably, but a
      // row written between two requests would otherwise shift the
      // window and repeat an entry.
      setItems((prev) => {
        const seen = new Set(prev.map((i) => i.id));
        return [...prev, ...next.filter((i) => !seen.has(i.id))];
      });
      setTotal(typeof data?.total === "number" ? data.total : 0);
      setHasMore(Boolean(data?.has_more));
    } catch (err) {
      if (seq !== seqRef.current) return;
      setLoadMoreError(
        err?.message || "Weitere Aktivitäten konnten nicht geladen werden."
      );
    } finally {
      if (seq === seqRef.current) setLoadingMore(false);
    }
  }, [entityType, entityId, activeValue, items.length, hasMore, loadingMore]);

  return {
    items,
    total,
    hasMore,
    filter,
    setFilter,
    loading,
    loadingMore,
    error,
    loadMoreError,
    retry: loadFirstPage,
    loadMore,
  };
}
